import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const ic = {
    plus: 'M12 5v14 M5 12h14',
    x: 'M18 6L6 18 M6 6l12 12',
    fuel: ['M3 22V9', 'M3 9a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v13', 'M13 2l4 4', 'M17 6h1a2 2 0 0 1 2 2v3a2 0 0 0 2 2h0'],
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    calendar: ['M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M16 2v4', 'M8 2v4', 'M3 10h18']
};

export default function FuelManagement({ user, operatorDetails }) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    // Dropdown data
    const [vehicles, setVehicles] = useState([]);
    const [fuelTypes, setFuelTypes] = useState([]);

    const [formData, setFormData] = useState({
        vehicleId: '',
        date: new Date().toISOString().split('T')[0],
        fuelType: '',
        quantity: '',
        cost: '',
        mileage: '',
        notes: ''
    });

    useEffect(() => {
        fetchRecords();
        loadDropdownData();
    }, [user]);

    const fetchRecords = async () => {
        setLoading(true);
        try {
            const opId = user?.operatorId || user?._id || 'all';
            const res = await axios.get(`${API}/fuel-records/${opId}`);
            setRecords(res.data);
        } catch (err) {
            console.error('Fetch fuel records error:', err);
        } finally {
        }
    };

    const loadDropdownData = async () => {
        const opId = user?.operatorId || user?._id || 'all';
        try {
            const [vRes, mRes] = await Promise.all([
                axios.get(`${API}/vehicles/${opId}`),
                axios.get(`${API}/master-data`)
            ]);
            setVehicles(vRes.data);
            setFuelTypes(mRes.data.filter(d => d.category === 'fuel-types'));

            // Set defaults
            if (mRes.data.length > 0) {
                const defaultFuel = mRes.data.find(d => d.category === 'fuel-types');
                if (defaultFuel) setFormData(prev => ({ ...prev, fuelType: defaultFuel.name }));
            }
        } catch (err) {
            console.error('Load dropdown error:', err);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const quantity = parseFloat(formData.quantity);
        const cost = parseFloat(formData.cost);
        const mileage = parseFloat(formData.mileage);

        if (isNaN(quantity) || isNaN(cost) || isNaN(mileage)) {
            alert("Please enter valid numbers for Quantity, Cost, and Mileage.");
            return;
        }

        try {
            const payload = {
                ...formData,
                operatorId: user?.operatorId || user?._id,
                quantity,
                cost,
                mileage
            };

            if (editingRecord) {
                await axios.put(`${API}/fuel-records/${editingRecord._id}`, payload);
            } else {
                await axios.post(`${API}/fuel-records`, payload);
            }

            closeModal();
            fetchRecords();
        } catch (err) {
            console.error('Save record error:', err);
            alert("Failed to save fuel record: " + (err.response?.data?.message || err.message));
        }
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setFormData({
            vehicleId: record.vehicleId?._id || '',
            date: new Date(record.date).toISOString().split('T')[0],
            fuelType: record.fuelType,
            quantity: record.quantity.toString(),
            cost: record.cost.toString(),
            mileage: record.mileage.toString(),
            notes: record.notes || ''
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingRecord(null);
        setFormData({
            vehicleId: '', date: new Date().toISOString().split('T')[0],
            fuelType: fuelTypes[0]?.name || '', quantity: '', cost: '', mileage: '', notes: ''
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this fuel record?')) return;
        try {
            await axios.delete(`${API}/fuel-records/${id}`);
            fetchRecords();
        } catch (err) {
            console.error('Delete error:', err);
            alert("Failed to delete record: " + err.message);
        }
    };

    return (
        <div className="fm-page">
            <style>{`
                .fm-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .fm-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .fm-title { font-size: 26px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .fm-subtitle { font-size: 14px; color: #64748b; margin: 0; }
                
                .fm-add-btn {
                    background: #2563eb; color: white; border: none; padding: 10px 22px;
                    border-radius: 8px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    font-size: 14px; box-shadow: 0 4px 12px rgba(37,99,235,0.2);
                }

                .fm-card { 
                    background: white; border-radius: 12px; border: 1px solid #e2e8f0; 
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03); overflow: hidden;
                }
                .fm-table { width: 100%; border-collapse: collapse; }
                .fm-table th { 
                    text-align: left; padding: 16px 20px; font-size: 12px; font-weight: 600; 
                    color: #64748b; text-transform: uppercase; border-bottom: 1px solid #f1f5f9; background: #f8fafc;
                }
                .fm-table td { padding: 16px 20px; font-size: 14px; color: #334155; border-bottom: 1px solid #f1f5f9; }
                .fm-table tr:hover { background: #fafbfc; }

                .fm-vehicle-badge { 
                    background: #eff6ff; color: #2563eb; font-weight: 700; padding: 4px 10px; border-radius: 6px; font-size: 12px;
                }
                .fm-date-cell { display: flex; align-items: center; gap: 8px; color: #64748b; font-size: 13px; }

                /* Modal */
                .fm-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .fm-modal { background: white; width: 550px; border-radius: 20px; padding: 30px; position: relative; }
                .fm-modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
                .fm-modal-title { font-size: 20px; font-weight: 800; color: #1e293b; margin: 0; }
                .fm-modal-close { cursor: pointer; color: #94a3b8; }
                
                .fm-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
                .fm-form-group { display: flex; flex-direction: column; gap: 6px; }
                .fm-form-group.full { grid-column: span 2; }
                .fm-label { font-size: 12px; font-weight: 700; color: #475569; }
                .fm-input, .fm-select {
                    padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 14px;
                    outline: none; transition: all 0.2s; background: #fff;
                }
                .fm-input:focus, .fm-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
                
                .fm-save-btn {
                    width: 100%; background: #2563eb; color: white; border: none; padding: 12px;
                    border-radius: 10px; font-weight: 700; cursor: pointer; font-size: 15px;
                }
                .fm-del-btn { 
                    border: none; background: none; color: #94a3b8; cursor: pointer; padding: 4px; border-radius: 4px;
                }
                .fm-del-btn:hover { background: #fee2e2; color: #ef4444; }
            `}</style>

            <div className="fm-header">
                <div>
                    <h1 className="fm-title">Fuel Management</h1>
                    <p className="fm-subtitle">Track fuel consumption, costs, and mileage across your fleet.</p>
                </div>
                <button className="fm-add-btn" onClick={() => { setEditingRecord(null); setShowModal(true); }}>
                    <Icon d={ic.plus} size={18} strokeWidth={2.5} /> Add Fuel Record
                </button>
            </div>

            <div className="fm-card">
                <table className="fm-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Vehicle</th>
                            <th>Fuel Type</th>
                            <th>Quantity (L)</th>
                            <th>Cost</th>
                            <th>Mileage (km)</th>
                            <th style={{ width: 80 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading fuel records...</td></tr>
                        ) : records.length === 0 ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No fuel records found.</td></tr>
                        ) : (
                            records.map(r => (
                                <tr key={r._id}>
                                    <td>
                                        <div className="fm-date-cell">
                                            <Icon d={ic.calendar} size={14} />
                                            {new Date(r.date).toLocaleDateString()}
                                        </div>
                                    </td>
                                    <td><span className="fm-vehicle-badge">{r.vehicleId?.plateNumber || 'N/A'}</span></td>
                                    <td>{r.fuelType}</td>
                                    <td style={{ fontWeight: 600 }}>{r.quantity} L</td>
                                    <td style={{ color: '#059669', fontWeight: 700 }}>{formatCurrency(r.cost, operatorDetails?.country)}</td>
                                    <td>{r.mileage?.toLocaleString()} km</td>
                                    <td style={{ display: 'flex', gap: '8px' }}>
                                        <button className="fm-del-btn" title="Edit" onClick={() => handleEdit(r)}>
                                            <Icon d={ic.edit} size={16} />
                                        </button>
                                        <button className="fm-del-btn" title="Delete" onClick={() => handleDelete(r._id)}>
                                            <Icon d={ic.trash} size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="fm-modal-overlay">
                    <div className="fm-modal">
                        <div className="fm-modal-header">
                            <h2 className="fm-modal-title">{editingRecord ? 'Edit Fuel Record' : 'Add Fuel Record'}</h2>
                            <div className="fm-modal-close" onClick={closeModal}>
                                <Icon d={ic.x} size={20} />
                            </div>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="fm-form-grid">
                                <div className="fm-form-group">
                                    <label className="fm-label">Vehicle</label>
                                    <select
                                        className="fm-select" required
                                        value={formData.vehicleId}
                                        onChange={e => setFormData({ ...formData, vehicleId: e.target.value })}
                                    >
                                        <option value="">Select Vehicle...</option>
                                        {vehicles.map(v => (
                                            <option key={v._id} value={v._id}>{v.plateNumber} ({v.make})</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="fm-form-group">
                                    <label className="fm-label">Date</label>
                                    <input
                                        type="date" className="fm-input" required
                                        value={formData.date}
                                        onChange={e => setFormData({ ...formData, date: e.target.value })}
                                    />
                                </div>
                                <div className="fm-form-group">
                                    <label className="fm-label">Fuel Type</label>
                                    <select
                                        className="fm-select" required
                                        value={formData.fuelType}
                                        onChange={e => setFormData({ ...formData, fuelType: e.target.value })}
                                    >
                                        {fuelTypes.map(f => (
                                            <option key={f._id} value={f.name}>{f.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="fm-form-group">
                                    <label className="fm-label">Quantity (Liters)</label>
                                    <input
                                        type="number" step="0.01" className="fm-input" required
                                        placeholder="0.00"
                                        value={formData.quantity}
                                        onChange={e => setFormData({ ...formData, quantity: e.target.value })}
                                    />
                                </div>
                                <div className="fm-form-group">
                                    <label className="fm-label">Total Cost ({operatorDetails?.defaultCurrency || 'USD'})</label>
                                    <input
                                        type="number" step="0.001" className="fm-input" required
                                        placeholder="0.000"
                                        value={formData.cost}
                                        onChange={e => setFormData({ ...formData, cost: e.target.value })}
                                    />
                                </div>
                                <div className="fm-form-group">
                                    <label className="fm-label">Odometer (km)</label>
                                    <input
                                        type="number" className="fm-input" required
                                        placeholder="Current mileage"
                                        value={formData.mileage}
                                        onChange={e => setFormData({ ...formData, mileage: e.target.value })}
                                    />
                                </div>
                                <div className="fm-form-group full">
                                    <label className="fm-label">Notes</label>
                                    <input
                                        type="text" className="fm-input"
                                        placeholder="Optional notes..."
                                        value={formData.notes}
                                        onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                    />
                                </div>
                            </div>
                            <button type="submit" className="fm-save-btn">
                                {editingRecord ? 'Update Fuel Record' : 'Save Fuel Record'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
